import { Injectable } from '@angular/core';
import {
  AmdocsAppsyncClientService,
  AmdocsGatewayClientService,
  AmdocsTimeService,
  AmdocsUserService,
} from 'amdocs-auth-package';
import {
  IAccessBridge,
  IAutocompleteItem,
  IEmployeeSearchHistory,
  IUserProfile,
} from 'amdocs-core-package';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import {
  ICountry,
  IUserHolidays,
  IUserHolidaysRes,
} from '../feature/calendar/calendar.service';
import { IBooking } from '../feature/home/cards/card-flex-seat/card-flex-seat.service';
import { IStatusTracker } from '../feature/home/cards/card-job/card-job.service';
import { IUserSites } from '../feature/it-accessory/it-accessory.service';
import { IJobList } from '../feature/jobs-page/jobs-page.service';
import { EmployeeDetails } from './emp-details.service';
import { IMeeting, IMeetingDetails } from './meeting.service';
import {
  IShortcutNotification,
  IUpdateGiftNotifications,
} from './shortcuts-notifications.service';

import {
  IBuilding,
  ICarWash,
  ICountrySites,
} from '../feature/guest-reservation/guest-reservation.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private userService: AmdocsUserService,
    private appSyncClient: AmdocsAppsyncClientService,
    private timeService: AmdocsTimeService,
    private appGw: AmdocsGatewayClientService
  ) { }

  public getShortCutsNotifications(): Observable<IShortcutNotification[]> {
    const params = {};
    const query = `
    query GetMyNotifications {
      shortcutNotifications {
        shortcutType
        notification {
          counter
        }
      }
    }`;
    return this.appSyncClient.call(query, 'GetMyNotifications').pipe(
      mergeMap(
        (response: {
          shortcutNotifications: IShortcutNotification[];
        }): Observable<IShortcutNotification[]> => {
          return of(response?.shortcutNotifications || []);
        }
      ),
      catchError((err) => throwError(() => err))
    );
  }

  public updateGiftNotifications(): Observable<IUpdateGiftNotifications> {
    const params = {
      count: 0,
    };
    const query = `mutation UpdateGiftNotifications($count: Int!) {
  updateGiftNotifications(count: $count) {
      notificationCounter
  }
}`;
    return this.appSyncClient
      .call(query, 'UpdateGiftNotifications', params)
      .pipe(
        mergeMap(
          (response: {
            updateGiftNotifications: IUpdateGiftNotifications;
          }): Observable<IUpdateGiftNotifications> => {
            return of(response?.updateGiftNotifications);
          }
        ),
        catchError((err) => throwError(() => err))
      );
  }

  public getUserSites(): Observable<IUserSites[]> {
    const query = `
query GetUserSites{
  userSites{
      name
      message
  }
}
`;
    return this.appSyncClient.call(query, 'GetUserSites').pipe(
      mergeMap(
        (response: { userSites: IUserSites[] }): Observable<IUserSites[]> => {
          return of(response?.userSites || []);
        }
      ),
      catchError((err) => throwError(() => err))
    );
  }

  public getMeetings(): Observable<IMeeting[]> {
    const query = `
query GetMeetings($appid:String) {
  getMeetings(appid:$appid) {
    meetings {
      id
      subject
      isAllDay
      isCancelled
      start
      end
      location
      organizer {
        emailAddress {
          address
          name
        }
      }
      onlineMeeting {
        joinUrl
        dialInNumber
      }
      onlineMeetingProvider
      isOnlineMeeting
       responseStatus {
            response
            time
        }
    }
  }
}
`;
    return this.appSyncClient.call(query, 'GetMeetings').pipe(
      mergeMap((response: { getMeetings: any }): Observable<IMeeting[]> => {
        return of(response?.getMeetings?.meetings || []);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  public getMeetingDetails(meetingId): Observable<IMeetingDetails> {
    const params = {
      id: meetingId,
    };
    const query = `
query GetMeetingDetails($id: String!, $appid:String) {
  getMeetingDetails(id: $id, appid:$appid) {
    attendees {
      emailAddress {
        address
        name
      }
      status {
        response
        time
      }
      type
    }
    body {
     contentType
     content
    }
    end
    id
    isAllDay
    isCancelled
    isOnlineMeeting
    lastModifiedDateTime
    location
    onlineMeeting {
      joinUrl
      dialInNumber
    }
    onlineMeetingProvider
    onlineMeetingUrl
    organizer {
      emailAddress {
        address
        name
      }
    }
    responseStatus {
      response
      time
    }
    showAs
    start
    subject
    webLink
  }
}

     `;
    return this.appSyncClient.call(query, 'GetMeetingDetails', params).pipe(
      mergeMap(
        (response: { getMeetingDetails: any }): Observable<IMeetingDetails> => {
          if (!response.getMeetingDetails.responseStatus) {
            response.getMeetingDetails.responseStatus = {
              response: null,
              time: null,
            };
          }
          return of(response.getMeetingDetails);
        }
      ),
      catchError((err) => throwError(() => err))
    );
  }

  public getEmployeeDetails(
    uuid: string,
    skipHistory: boolean = true
  ): Observable<EmployeeDetails> {
    const params = {
      id: uuid,
      skipHistory,
    };
    const query = `
     query GetEmployeeDetails($id: String!, $skipHistory: Boolean, $appid: String) {
  employeeDetails(id: $id, skipHistory: $skipHistory, appid: $appid) {
    id
    displayName
    mail
    userPrincipalName
    jobTitle
    businessPhones
    mobilePhone
    officeLocation
    state
    department
    userType
    employeeId
    country
    city
    chatTeamsUrl
    callSkypeUrl
    chatSkypeUrl
  }
}
     `;
    return this.appSyncClient.call(query, 'GetEmployeeDetails', params).pipe(
      mergeMap(
        (response: {
          employeeDetails: EmployeeDetails;
        }): Observable<EmployeeDetails> => {
          if (!skipHistory) {
            this.refreshUserSearchHistory();
          }
          return of(response.employeeDetails);
        }
      )
    );
  }

  public refreshUserSearchHistory(): void {
    const query = `
    query employeeSearchHistory {
      employeeSearchHistory {
        employees {
          id
          jobTitle
          displayName
        }
      }
    }`;
    this.appSyncClient
      .call(query, 'employeeSearchHistory')
      .pipe(
        mergeMap(
          (response: {
            employeeSearchHistory: IEmployeeSearchHistory;
          }): Observable<IEmployeeSearchHistory> => {
            this.userService.user.employeeSearchHistory =
              response.employeeSearchHistory;
            return of(this.userService.user.employeeSearchHistory);
          }
        )
      )
      .subscribe();
  }

  public getReportedTime(TZQueryInput): Observable<any> {
    const params = {
      input: TZQueryInput,
    };
    const query = `
     query GetReportedTime($input: TZQueryInput!) {
  querywhdata(input: $input) {
    ... on TZQuery {
      TransactionId
      TransactionStatus
      TransactionMessage
      RoleToken
      EmployeeStartDate
      EmployeeDetails {
        EmployeeStartDate
        Name
        Location
        VacationBalance
        SickDaysBalance
        ManagerType
        NotificationsAlert
      }
      Notifications {
        Type
        Message
      }
      Data {
        WEEK {
          WEEK_APPROVED
          WEEK_END
          WEEK_NO
          WEEK_START
          WEEK_STATE
          DAYS {
            REP_DATE {
              ABSENCE_HOURS
              ABSENCE_LINES {
                ABSENCE_CODE
                ABSENCE_DESC
                NEGATIVE
                ABSENCE_HOURS
                ICON
                LOCKED
              }
              DATE
              LINES {
                LINE_NO
                  IN
                  OUT
                }
              }
            }
          }
        }
      }

    ... on ErrorUnauthorized {
      httpCode
\t  httpMessage
      moreInformation
    }

    ... on ErrorBadRequest {
      message
    }
  }
}
     `;
    return this.appSyncClient.call(query, 'GetReportedTime', params).pipe(
      mergeMap((response: { querywhdata: any }): Observable<any> => {
        return of(response.querywhdata);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  public saveHour(TZQueryInput): Observable<any> {
    const params = {
      input: TZQueryInput,
    };
    const query = `
mutation SaveTime($input: TZSaveInput!) {
  savewhdata(input: $input) {
    ... on TZSave {
      TransactionId
      TransactionStatus
      TransactionMessage
      Data {
        WEEK {
          WEEK_APPROVED
          WEEK_NO
          WEEK_MESSAGES {
            WEEK_MSG
            WEEK_MSG_TYPE
          }
          DAYS {
            REP_DATE {
              DATE
              MESSAGES {
                foo
              }
              ABSENCE_LINES {
                foo
              }
              LINES {
                LINE_NO
                TMP_ID
                MSG_TYPE
                MSG_STATUS
                MSG_DESC
              }
            }
          }
        }
      }
    }

    ... on ErrorUnauthorized {
      httpCode
      httpMessage
      moreInformation
    }

    ... on ErrorBadRequest {
      message
    }
  }
}
     `;
    return this.appSyncClient.call(query, 'SaveTime', params).pipe(
      mergeMap((response: { savewhdata: any }): Observable<any> => {
        return of(response.savewhdata);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  public setAccessBridge(bridgeId: string): Observable<IAccessBridge> {
    const params = {
      id: bridgeId,
    };
    const query = `
mutation SelectAccessBridge($id: String!) {
  selectAccessBridge(id: $id){
    id
    displayName
    dialInNumber
  }
}
     `;
    return this.appSyncClient.call(query, 'SelectAccessBridge', params).pipe(
      mergeMap(
        (response: {
          selectAccessBridge: IAccessBridge;
        }): Observable<IAccessBridge> => {
          return of(response.selectAccessBridge);
        }
      ),
      catchError((err) => throwError(() => err))
    );
  }

  public getAccessBridge(): Observable<IAccessBridge[]> {
    const params = {};
    const query = `
    query ListAccessBridges {
  listAccessBridges {
    id
    displayName
    dialInNumber
  }
}
     `;
    return this.appSyncClient.call(query, 'ListAccessBridges', params).pipe(
      mergeMap(
        (response: {
          listAccessBridges: IAccessBridge[];
        }): Observable<IAccessBridge[]> => {
          return of(response?.listAccessBridges || []);
        }
      )
    );
  }

  public getUserProfile(): Observable<IUserProfile> {
    const params = {};
    const query = `
    query GetUserProfile {
      userProfile {
        accessBridge {
          id
          displayName
          dialInNumber
        }
      }
    }
     `;
    return this.appSyncClient.call(query, 'GetUserProfile', params).pipe(
      mergeMap(
        (response: { userProfile: IUserProfile }): Observable<IUserProfile> => {
          return of(response.userProfile ? response.userProfile : null);
        }
      )
    );
  }

  public getNotificationCodes(): Observable<any> {
    const params = {};
    const query = `
    query GetUserSettings {
      userSettings {
        notificationCodes
      }
    }
    `;
    return this.appSyncClient.call(query, 'GetUserSettings', params).pipe(
      mergeMap((response: { notificationCodes: [] }): Observable<string[]> => {
        return of(response?.notificationCodes || []);
      })
    );
  }

  public subscribeToPushNotifications(
    subscriptionResponse: any
  ): Observable<boolean> {
    const query = `
            mutation SubscribeToReceiveNotifications($input: PushSubscriptionInput!){
              createPushSubscription(input: $input) {
                  endpoint
                  expirationTime
                  keys {
                    p256dh
                    auth
                  }
              }
            }
            `;
    const params = {
      input: subscriptionResponse,
    };
    return this.appSyncClient
      .callCNS(query, 'SubscribeToReceiveNotifications', params)
      .pipe(
        mergeMap((response): Observable<boolean> => {
          return of(true);
        })
      );
  }

  public unsubscribeFromPushNotifications(
    authKey: string
  ): Observable<boolean> {
    const query = `
      mutation DeleteSubscription($auth: String!){
          deleteSubscription(identifier: $auth)
        }
      `;
    const params = {
      auth: authKey,
    };
    return this.appSyncClient.callCNS(query, 'DeleteSubscription', params).pipe(
      mergeMap((response): Observable<boolean> => {
        return of(true);
      })
    );
  }

  public getStatusTracker(input): Observable<IStatusTracker> {
    const query = `
query StatusTracker($input: JobStatusTrackerInput!) {
  statusTracker(input: $input) {
        totalApplication
        totalActiveApplication
    applications {
        candidateId,
        jobReqId,
        jobTitle,
        SFstatus,
        applicationDate,
        lastModifiedDateTime,
        Kypros,
        recruiterLastName,
        recruitMail,
        SFcandidateLabel,
        SFappStatusId,
        SNOWstatusForUser
    }
  }
}
`;
    return this.appSyncClient.call(query, 'StatusTracker', { input }).pipe(
      mergeMap((response: IStatusTracker): Observable<IStatusTracker> => {
        return of(response);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  public getJobList(input): Observable<IJobList> {
    const query = `
query JobList($input: JobListInput!) {
  jobList(input: $input) {
    numFound
  }
}
`;
    return this.appSyncClient.call(query, 'JobList', { input }).pipe(
      mergeMap((response: { jobList: IJobList }): Observable<IJobList> => {
        return of(response.jobList);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  public updateUserAppDownloadStatus(
    appDownloaded: boolean
  ): Observable<IAccessBridge> {
    const params = {
      appDownloaded,
    };
    const query = `
      mutation UpdateUserAppDownloadStatus($appDownloaded: Boolean!) {
        updateUserAppDownloadStatus(appDownloaded: $appDownloaded) {
          msg
          appDownloaded
        }
      }
     `;
    return this.appSyncClient
      .call(query, 'UpdateUserAppDownloadStatus', params)
      .pipe(
        mergeMap((response): Observable<any> => {
          return of(response);
        }),
        catchError((err) => throwError(() => err))
      );
  }

  public getBookings(): Observable<IBooking[]> {
    const query = `query GetMyLatestBooking {
\tmyFutureBookings {
        bookingTime {
            start
            end
        }
        bookedBy {
            code
            name
        }
        code
        location {
            building {
                code
                name
            }
            country {
                code
                name
            }
            floor {
                code
                name
            }
            wingGroup {
                code
                name
            }
        }
        roomCode
        seatCode
        seatName
    }
}`;
    return this.appSyncClient.call(query, 'GetMyLatestBooking', null).pipe(
      mergeMap((response): Observable<IBooking[]> => {
        return of(response.myFutureBookings);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  public getUserHolidays(
    countryCode: string[],
    year: string,
    month: string
  ): Observable<IUserHolidays[]> {
    const params = { input: { countryCode, year, month } };
    const query = `query GetUserHolidays($input: HolidaysInput!) {
  holidays(input: $input) {
  success
    data {
        country
        holidaysList {
            year
            month
            day
            titles
        }
    }
  }
}`;
    return this.appSyncClient.call(query, 'GetUserHolidays', params).pipe(
      mergeMap(
        (response: {
          holidays: IUserHolidaysRes;
        }): Observable<IUserHolidays[]> => {
          return of(response?.holidays?.data);
        }
      ),
      catchError((err) => throwError(() => err))
    );
  }
  public getCountries(): Observable<ICountry[]> {
    const query = `query GetCountries {
  countries {
    countryCode
    countryName
    regionCode
    countryCodeSecondary
    countryCodeNum
  }
}`;
    return this.appSyncClient.call(query, 'GetCountries').pipe(
      mergeMap(
        (response: { countries: ICountry[] }): Observable<ICountry[]> => {
          return of(response?.countries);
        }
      ),
      catchError((err) => throwError(() => err))
    );
  }

  //learning API
  public getContinueLearning() {
    const query = `query ContinueLearning {
         continueLearning {
          filterByDuration {
          durationName
          durationID
          durationCount
          }
          items {
            		userName
                userCode
                empName
                cpntId
                cpntTypId
                courseName
                courseDesc
                mcDescHtml
                courseLength
                delMethod
                ecStatus
                completeStatus
                courseCategory
                mobilePic
                bigPic
                itemRate
                numOfRate
                empLabel
                empButton
                filterDelMethod
                filterDuration
          }
          success
          }
        }`;
    return this.appSyncClient.call(query, 'ContinueLearning').pipe(
      mergeMap((response: any) => {
        return of(response);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  // Get Attachment List
  public getAttachmentsList(taskId: number = 0) {
    const query = `query GetOLFAttachmentList {
        attachmentlist(taskId:${taskId}) {
          infoMessage
          errorCode
          attachments {
            contentLength
            contentType
            fileName
            id
          }
          responseDate
          responseStatus
        }
      }`;
    return this.appSyncClient.call(query, 'GetOLFAttachmentList').pipe(
      mergeMap((response: any) => {
        return of(response);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  // GetAllachmentContent
  getAllachmentContent(attachmentId: string = '') {
    const query = `query GetOLFAttachmentContent {
        attchmentContent(attachmentId : "${attachmentId}") {
           attachmentURL

         }
       }`;
    return this.appSyncClient.call(query, 'GetOLFAttachmentContent').pipe(
      mergeMap((response: any) => {
        return of(response);
      }),
      catchError((err) => throwError(() => err))
    );
  }
  getCountrySites(countryCode: string = '') {
    const query = `query GetGuestSiteLocationQuery {
      sitesLocation(countrycode : "${countryCode}") {
        name
        code
         }
       }`;
    return this.appSyncClient.call(query, 'GetGuestSiteLocationQuery').pipe(
      mergeMap(
        (response: {
          sitesLocation: ICountrySites[];
        }): Observable<ICountrySites[]> => {
          return of(response?.sitesLocation || []);
        }
      ),
      catchError((err) => throwError(() => err))
    );
  }
  getAllParkings(): Observable<IBuilding[]> {
    const query = `query GetAllParkings {
  getAllParkings {
    buildingCode
    buildingName
    siteID
    siteNameEng
    parkings {
      base
      parkingCode
      parkingName
      waitingList
      status
    }
  }
}`;

    return this.appSyncClient.call(query, 'GetAllParkings').pipe(
      mergeMap(
        (response: {
          getAllParkings: IBuilding[];
        }): Observable<IBuilding[]> => {
          return of(response?.getAllParkings || []);
        }
      ),
      catchError((err) => throwError(() => err))
    );
  }

  public findMatchingSite(
    siteListItems: IAutocompleteItem[],
    userOffice: string
  ) {
    if (!siteListItems || !userOffice) {
      return null;
    }

    let selectedSiteItem = siteListItems.find(item =>
      item?.name.toLowerCase().includes(userOffice.toLowerCase())
    );
    if (!selectedSiteItem && userOffice.includes(',')) {
      const officeFirstPart = userOffice.split(',')[0].trim().toLowerCase();
      selectedSiteItem = siteListItems.find(item =>
        item?.name.toLowerCase().includes(officeFirstPart)
      );
    }

    return selectedSiteItem || null;
  }

  public SubmitGuestReservation(GuestReservationInput): Observable<any> {
    const params = {
      input: GuestReservationInput,
    };
    //console.log(GuestReservationInput)
    const query = `mutation SubmitGuestReservation($input: GuestReservationInput!) {
      submitGuestReservation(input: $input) {
        InfoMessage
      ResponseDate
        ResponseStatus
      ErrorCode
        RequestId
        RequestUrl
      }
    }`;
    return this.appSyncClient
      .call(query, 'SubmitGuestReservation', params)
      .pipe(
        mergeMap((res: { data: any }): Observable<any> => {
          //console.log(res)
          return of(res);
        }),
        catchError((err) => {
          return of({ error: err });
        })
      );
  }

  public updateLearningPopupStatus(
    appDownloaded: boolean
  ): Observable<IAccessBridge> {
    const params = {
      appDownloaded,
    };
    const query = `
        mutation SetLearningFlag($appDownloaded: Boolean!) {
          setLearningFlag(learningPopupFlag: $appDownloaded) {
            msg
            learningPopupFlag
          }
        }
       `;
    return this.appSyncClient.call(query, 'SetLearningFlag', params).pipe(
      mergeMap((response): Observable<any> => {
        return of(response);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  public submitCarPerDayRequest(carPerDayData: any): Observable<any> {
    const params = {
      carPerDayInput: carPerDayData,
    };
    const query = `
      mutation SubmitCarPerDayRequest($carPerDayInput: CarPerDayInput!) {
        submitCarPerDayRequest(carPerDayInput: $carPerDayInput) {
          success
          message
          requestId
        }
      }`;
    return this.appSyncClient
      .call(query, 'SubmitCarPerDayRequest', params)
      .pipe(
        mergeMap((response): Observable<any> => {
          return of(response);
        }),
        catchError((err) => throwError(() => err))
      );
  }

  getAllCarWashTickets(): Observable<any> {
    const query = `query GetAllCarWashTickets {
  getAllCarWashTickets {
    carCode
    closeId
    closureTime
    comments
    createDate
    empId
    keysLocation
    objectId
    parkingCode
    parkingSpot
    returnBy
    ticketId
    ticketStatusId
    trunckWash
    waitingListYn
  }
}`;

    return this.appSyncClient.call(query, 'GetAllCarWashTickets').pipe(
      mergeMap((response: { getAllCarWashTickets: any }): Observable<any> => {
        return of(response?.getAllCarWashTickets || []);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getAllCarRentTicket(): Observable<any> {
    const query = `query GetAllCarRentTicket {
	  getAllCarRentTicket {
	    categoryId
	    createDate
	    empId
	    pickupDate
	    returnDate
	    siteId
	    statusId
	    ticketId
	  }
	}`;

    return this.appSyncClient.call(query, 'GetAllCarRentTicket').pipe(
      mergeMap((response: { getAllCarRentTicket: any }): Observable<any> => {
        return of(response?.getAllCarRentTicket || []);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getAllReportAccident(): Observable<any> {
    const query = `query GetAllReportAccident {
  getAllReportAccident {
    accidentCity
    accidentDate
    addEmails
    additionalInj
    additionalWit
    createDate
    driverLicense
    driverNameHeb
    ticketType
    ticketStatusId
    ticketId
    thirdPartyYn
    submitDate
    streetName
    witnessesYn
    witnessName
    witnessMobile
    witnessId
    witnessAddress
    tppolicyNumber
    tplpnumber
    tpinsuranceCo
    tpdriverTz
  }
}
`;

    return this.appSyncClient.call(query, 'GetAllReportAccident').pipe(
      mergeMap((response: { getAllReportAccident: any }): Observable<any> => {
        return of(response?.getAllReportAccident || []);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getReportAccidentByTicketID(ticketId: number) {
    const query = `query GetReportAccidentByTicketID {
  getAccidentTicket(TicketId:${ticketId}) {
    accidentCity
    accidentDate
    addEmails
    additionalInj
    additionalWit
    driverLicense
    eventDesc
    injuredId
    injuredAddress
    submitDate
    ticketId
    ticketStatusId
    ticketType
    tpcarModel
    tpdamageDesc
    tplpnumber
    witnessesYn
    witnessName
    witnessMobile
    witnessId
    witnessAddress
    createDate
    empEmail
    empId
    lpnumber
  }
}`;
    return this.appSyncClient.call(query, 'GetReportAccidentByTicketID').pipe(
      mergeMap((response: { getAccidentTicket: any }): Observable<any> => {
        return of(response?.getAccidentTicket || []);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getCarwashTicketByID(ticketId: number) {
    const query = `query GetCarwashTicketByID {
  getCarwashTicketByID(TicketId:${ticketId}) {
    carCode
    closeId
    closureTime
    comments
    empId
    objectId
    parkingCode
    parkingSpot
    returnBy
    ticketId
    ticketStatusId
    trunckWash
    waitingListYn
    keysLocation
    createDate
  }
}`;
    return this.appSyncClient.call(query, 'GetCarwashTicketByID').pipe(
      mergeMap((response: { getCarwashTicketByID: any }): Observable<any> => {
        return of(response?.getCarwashTicketByID || []);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getCarRentByTicketID(ticketId: number) {
    const query = `query GetCarRentByTicketID {
  getCarRentByTicketID(TicketId: ${ticketId}) {
    categoryId
    createDate
    empId
    pickupDate
    returnDate
    siteId
    statusId
    ticketId
    siteName
    categryName
  }
}
`;
    return this.appSyncClient.call(query, 'GetCarRentByTicketID').pipe(
      mergeMap((response: { getCarRentByTicketID: any }): Observable<any> => {
        return of(response?.getCarRentByTicketID || []);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getAllCarFaultReport(): Observable<any> {
    const query = `query GetAllCarFaultReport {
  getAllCarFaultReport {
    errors
    message
    success
    data {
      branch
      comments
      createDate
      empEmail
      empId
      empMobile
      empNameHeb
      faultTypes
      lpnumber
      otherFaultDesc
      statusId
      ticketId
    }
  }
}`;

    return this.appSyncClient.call(query, 'GetAllCarFaultReport').pipe(
      mergeMap((response: { getAllCarFaultReport: any }): Observable<any> => {
        return of(response?.getAllCarFaultReport ?? null);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getCarFaultReport(ticketId: number): Observable<any> {
    const query = `query GetCarFaultReport {
  getCarFaultReport(TicketId: ${ticketId}) {
    data {
      ticketId
      empId
      lpnumber
      branch
      createDate
      statusId
      faultTypes
      otherFaultDesc
      comments
      empNameHeb
      empMobile
      empEmail
    }
    errors
    message
    success
  }
}`;
    return this.appSyncClient.call(query, 'GetCarFaultReport').pipe(
      mergeMap((response: { getCarFaultReport: any }): Observable<any> => {
        return of(response?.getCarFaultReport ?? null);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getAllSchedules(): Observable<any> {
    const query = `query GetAllSchedules {
  getAllSchedules {
    success
    message
    data {
      lpNumber
      empNameHeb
      empMobile
      empEmail
      ticketID
      statusID
      empID
      branch
      createDate
      serviceDate
      serviceType
      comments
    }
    errorCode
    errors
  }
}`;

    return this.appSyncClient.call(query, 'GetAllSchedules').pipe(
      mergeMap((response: { getAllSchedules: any }): Observable<any> => {
        return of(response?.getAllSchedules ?? null);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getCarScheduleByTicketID(ticketId: number): Observable<any> {
    const query = `query GetCarScheduleByTicketID {
  getCarScheduleByTicketID(TicketId: ${ticketId}) {
    data {
      branch
      comments
      createDate
      empEmail
      empID
      empMobile
      empNameHeb
      lpNumber
      serviceDate
      serviceType
      statusID
      ticketID
    }
    errorCode
    errors
    message
    success
  }
}`;

    return this.appSyncClient.call(query, 'GetCarScheduleByTicketID').pipe(
      mergeMap((response: { getCarScheduleByTicketID: any }): Observable<any> => {
        return of(response?.getCarScheduleByTicketID ?? null);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  public cancelCarSchedule(ticketId: number): Observable<any> {
    const query = `mutation CancelCarScheduleMutation {
  cancelCarSchedule(TicketId: ${ticketId}) {
    data
    errorCode
    errors
    message
    success
  }
}`;

    return this.appSyncClient.call(query, 'CancelCarScheduleMutation').pipe(
      mergeMap((response): Observable<any> => {
        return of(response);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  public cancelCarPerDayOrder(ticketId: number): Observable<any> {
    const query = `
      mutation CancelCarPerDayMutation{
        cancelCarPerDay(TicketId: "${ticketId}") {
          message
          ticketID
        }
      }`;
    return this.appSyncClient.call(query, 'CancelCarPerDayMutation').pipe(
      mergeMap((response): Observable<any> => {
        return of(response);
      }),
      catchError((err) => throwError(() => err))
    );
  }
}
